import { useContext, useState } from "react";
import { contactInformation } from "../constants.js";
import { SiteContentContext } from "../context/SiteContentContext";
import { contact } from "../httpService";
import Button from "./button.jsx";
import ContentText from "./contentText.jsx";
import Heading from "./heading.jsx";
import { OFFICES, SwedenMap } from "./swedenMap.jsx";

export default function ContactModal() {
  const { getContent } = useContext(SiteContentContext);
  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", message: "" });
  const [status, setStatus] = useState(null);
  const [sending, setSending] = useState(false);

  function handleChange(e) {
    setForm({ ...form, [e.target.name]: e.target.value });
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!(form.name.trim() && form.email.trim() && form.message.trim() && form.phone.trim()))
      return;

    setSending(true);
    setStatus(null);
    try {
      await contact({
        replyTo: form.email,
        subject: `Kontaktförfrågan från ${form.name}${form.company ? `, ${form.company}` : ""}${form.phone ? ` (${form.phone})` : ""}`,
        text: form.message,
      });
      setStatus("success");
      setForm({ name: "", company: "", email: "", phone: "", message: "" });
    } catch {
      setStatus("error");
    } finally {
      setSending(false);
    }
  }

  return (
    <div>
      {/* Blue info section: text left, map right */}
      <div className="bg-primary text-white px-5 py-5 sm:py-8 flex gap-4">
        <div className="flex-1 flex flex-col gap-3 min-w-0">
          <Heading
            type="h1"
            size="text-xl sm:text-3xl"
            bg="bg-transparent"
            p="0"
            mb="0"
            sx="shadow-none"
          >
            <ContentText contentKey="contact.heading" fallback="Fler frågor?" />
          </Heading>
          <p className="text-white/80 text-sm">
            <ContentText
              contentKey="contact.subheading"
              fallback="Kontakta oss så hjälper vi dig vidare!"
            />
          </p>
          <div className="grid grid-cols-2 gap-x-4 gap-y-4 mt-4">
            {OFFICES.map((office) => (
              <div key={office.city}>
                <p className="font-semibold text-base">{office.city}</p>
                <p className="text-primary200 text-sm">{office.subtitle}</p>
              </div>
            ))}
            <div className="col-span-2">
              <a
                href={`tel:${contactInformation.telephoneNumber}`}
                className="hover:underline text-base flex items-center gap-1.5"
              >
                <svg
                  className="w-4 h-4 shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                  />
                </svg>
                {contactInformation.telephoneNumber}
              </a>
            </div>
            <div className="col-span-2">
              <a
                href={`mailto:${contactInformation.email}`}
                className="hover:underline text-sm flex items-center gap-1.5"
              >
                <svg
                  className="w-4 h-4 shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
                {contactInformation.email}
              </a>
            </div>
          </div>
        </div>
        <div className="hidden sm:block w-36 shrink-0">
          <SwedenMap />
        </div>
      </div>

      {/* Form section */}
      <div className="bg-white px-5 py-5 sm:py-8">
        <form onSubmit={handleSubmit} className="flex flex-col gap-3" data-testid="contact-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input
              type="text"
              name="name"
              placeholder={getContent("contact.name_placeholder", "Namn")}
              value={form.name}
              onChange={handleChange}
              required
              className="border border-border-default rounded px-3 py-2 bg-surface-muted focus:outline-none focus:ring-2 focus:ring-primary text-sm"
              data-testid="contact-name"
            />
            <input
              type="email"
              name="email"
              placeholder={getContent("contact.email_placeholder", "E-post")}
              value={form.email}
              onChange={handleChange}
              required
              className="border border-border-default rounded px-3 py-2 bg-surface-muted focus:outline-none focus:ring-2 focus:ring-primary text-sm"
              data-testid="contact-email"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input
              type="text"
              name="company"
              placeholder={getContent("contact.company_placeholder", "Företag")}
              value={form.company}
              onChange={handleChange}
              className="border border-border-default rounded px-3 py-2 bg-surface-muted focus:outline-none focus:ring-2 focus:ring-primary text-sm"
              data-testid="contact-company"
            />
            <input
              type="tel"
              name="phone"
              placeholder={getContent("contact.phone_placeholder", "Telefon")}
              value={form.phone}
              onChange={handleChange}
              required
              className="border border-border-default rounded px-3 py-2 bg-surface-muted focus:outline-none focus:ring-2 focus:ring-primary text-sm"
              data-testid="contact-phone"
            />
          </div>
          <textarea
            name="message"
            placeholder={getContent("contact.message_placeholder", "Meddelande")}
            rows={3}
            value={form.message}
            onChange={handleChange}
            required
            className="border border-border-default rounded px-3 py-2 bg-surface-muted focus:outline-none focus:ring-2 focus:ring-primary resize-y text-sm"
            data-testid="contact-message"
          />
          <Button
            type="submit"
            size="pill"
            loading={sending}
            sx="self-center"
            data-testid="contact-submit"
          >
            {sending ? (
              <ContentText contentKey="contact.sending" fallback="Skickar..." />
            ) : (
              <ContentText contentKey="contact.submit_button" fallback="Skicka" />
            )}
          </Button>
          {status === "success" && (
            <p className="text-green-600 text-sm text-center" aria-live="polite">
              <ContentText
                contentKey="contact.success_message"
                fallback="Meddelandet har skickats!"
              />
            </p>
          )}
          {status === "error" && (
            <p className="text-red-600 text-sm text-center" aria-live="polite">
              <ContentText
                contentKey="contact.error_message"
                fallback="Något gick fel. Försök igen."
              />
            </p>
          )}
        </form>
      </div>
    </div>
  );
}
